import { api, apiRequest } from "./api";
import type {
  ServiceCategory,
  ServiceByCategory,
  ServiceDetail,
  ServiceDetailEnvelope,
  ServiceStaffMember,
  StaffPaginatedResponse,
} from "@/types/services";

// ─── Categories ──────────────────────────────────────────────────────────────

/**
 * GET /internal/categories/
 * Returns the full list of service categories (departments).
 */
export async function fetchCategories(): Promise<ServiceCategory[]> {
  return apiRequest(() => api.get<ServiceCategory[]>("/internal/categories/"));
}

/**
 * POST /internal/categories/
 * Creates a new service category.
 */
export async function createCategory(
  formData: FormData
): Promise<ServiceCategory> {
  return apiRequest(() =>
    api.post<ServiceCategory>("/internal/categories/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  );
}

// ─── Services ─────────────────────────────────────────────────────────────────

export interface CategoryWithServices {
  name: string;
  services: ServiceByCategory[];
}

/**
 * GET /internal/categories/services/?category_ids={ids}
 * Returns admin-manageable services, including inactive services.
 * If categoryIds is empty, returns all non-deleted services across categories.
 */
export async function fetchServicesByCategoryIds(
  categoryIds: string[]
): Promise<ServiceByCategory[]> {
  const query =
    categoryIds.length > 0 ? `?category_ids=${categoryIds.join(",")}` : "";
  const response = await apiRequest(() =>
    api.get<ServiceByCategory[]>(`/internal/categories/services/${query}`)
  );

  if (!Array.isArray(response)) return [];
  return response.map((service) => ({
    ...service,
    category: service.category_name ?? service.category ?? "Unknown",
    category_name: service.category_name ?? service.category ?? "Unknown",
    cost:
      (service as any).price !== undefined
        ? String((service as any).price)
        : service.cost,
  }));
}

/**
 * GET /internal/categories/services/{serviceId}
 * API returns { "data": { ...service fields... } }.
 * apiRequest unwraps response.data → gives us { data: ServiceDetail }.
 * We then extract the inner .data to return ServiceDetail.
 */
export async function fetchServiceDetail(
  serviceId: string
): Promise<ServiceDetail> {
  const envelope = await apiRequest<ServiceDetailEnvelope>(() =>
    api.get<ServiceDetailEnvelope>(`/internal/categories/services/${serviceId}`)
  );
  return envelope.data;
}

/**
 * POST /internal/categories/services/
 * Creates a new service.
 */
export async function createService(
  formData: FormData
): Promise<ServiceByCategory> {
  return apiRequest(() =>
    api.post<ServiceByCategory>("/internal/categories/services/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  );
}

/**
 * PUT /internal/categories/services/{serviceId}/
 * Updates an existing service.
 */
export async function updateService(
  serviceId: string,
  formData: FormData
): Promise<ServiceByCategory> {
  return apiRequest(() =>
    api.put<ServiceByCategory>(
      `/internal/categories/services/${serviceId}/`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    )
  );
}

// ─── Staff (for service assignment) ─────────────────────────────────────────

/**
 * GET /internal/users/
 * Returns a paginated response { count, next, previous, results: [...] }.
 * We extract .results to return the flat list of staff members.
 */
export async function fetchStaffForService(): Promise<ServiceStaffMember[]> {
  const response = await apiRequest<StaffPaginatedResponse>(() =>
    api.get<StaffPaginatedResponse>("/internal/users/", {
      params: { page_size: 200 }, // large page to get all staff
    })
  );
  // Handle both paginated (results[]) and flat array responses gracefully
  if (Array.isArray(response)) {
    return response as unknown as ServiceStaffMember[];
  }
  return response?.results ?? [];
}
